import React, { Fragment, ReactNode, useState, useCallback, useMemo, useEffect, useRef, forwardRef } from 'react';
import { genericMemo, mergeRefs, useIntersectionListener, useMountEffect } from '@direct-frontend/stdlib';
import { getPlatformStyles } from '@direct-frontend/platform-code';

import { HStack, VStack } from '../Stack';
import { TextGeneric } from '../TextGeneric/TextGeneric';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { Button, ButtonProps } from '../Button';
import { IconVerticalMenu } from '../../icons';
import { cn } from '../../lib/css/className/className';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { Breadcrumbs } from '../Breadcrumbs';
import { HelpProps } from '../Help';

import { useSyncHeight } from './hooks/useSyncHeight';
import { PageHeadAddon, PageHeadProps, RenderMenuProps } from './types';
import { PageHeadWrapper } from './PageHeadWrapper/PageHeadWrapper';
import { PageHeadImage } from './PageHeadImage/PageHeadImage';
import { normalizeAddonLeft, normalizeButtons } from './helpers';

getPlatformStyles({ mobile: import('./PageHead@mobile.css') });

const cls = cn('PageHead');

const PageHead = genericMemo(function PageHead(props: PageHeadProps) {
    const { strategy = 'expandedStaticCollapsedFixed', scrollableRef } = props;

    const placeholderForFixedRef = useRef<null | HTMLDivElement>(null);
    const [fixedHeaderElement, setFixedHeaderElement] = useState<HTMLDivElement | null>(null);
    const intersectionListenerTargetRef = useRef(null);

    // scrollableRef может быть передан как useRef(null) — тогда .current заполняется после маунта,
    // но без ре-рендера. Откладываем создание IntersectionObserver до маунта, чтобы к тому моменту useSyncHeight
    // успел выставить высоту placeholder и IntersectionObserver сразу получил корректный isIntersected.
    const [isScrollableRefAvailable, setIsScrollableRefAvailable] = useState(Boolean(scrollableRef?.current));

    useMountEffect(() => {
        if (scrollableRef?.current && !isScrollableRefAvailable) {
            setIsScrollableRefAvailable(true);
        }
    });

    const isIntersectionListenerEnabled =
        isScrollableRefAvailable &&
        (strategy === 'expandedStaticCollapsedFixed' || strategy === 'expandedFixedCollapsedFixed');

    const { isIntersected, isInitialized: isIntersectionListenerInitialized } = useIntersectionListener({
        root: scrollableRef,
        target: intersectionListenerTargetRef,
        isEnabled: isIntersectionListenerEnabled,
        threshold: 0,
    });
    const isCollapsedVersionEnabled =
        strategy === 'alwaysCollapsedStatic' ||
        strategy === 'alwaysCollapsedSticky' ||
        strategy === 'alwaysCollapsedFixed' ||
        Boolean(isIntersectionListenerEnabled && isIntersectionListenerInitialized);

    const isFullVersionEnabled =
        !strategy || strategy === 'expandedStaticCollapsedFixed' || strategy === 'expandedFixedCollapsedFixed';

    const isFullVersionVisible = isFullVersionEnabled && (!isIntersectionListenerInitialized || isIntersected);

    const isCollapsibleBehaviour =
        strategy === 'expandedStaticCollapsedFixed' || strategy === 'expandedFixedCollapsedFixed';

    const isFixedStrategy = strategy === 'alwaysCollapsedFixed' || strategy === 'expandedFixedCollapsedFixed';

    useSyncHeight(fixedHeaderElement, placeholderForFixedRef, { shouldSkip: !isFixedStrategy });

    if (isFixedStrategy) {
        // фиксированная версия шапки
        return (
            <>
                {/**
                 * Элемент, который держит место для фиксированной шапки
                 * Его высота синхронизируется с высотой шапки
                 */}
                <div ref={mergeRefs(placeholderForFixedRef, intersectionListenerTargetRef)} />
                <PageHeadMobile
                    {...props}
                    isCollapsedVersionEnabled={isCollapsedVersionEnabled}
                    isFullVersionEnabled={isFullVersionEnabled}
                    isFullVersionVisible={isFullVersionVisible}
                    isCollapsibleBehaviour={isCollapsibleBehaviour}
                    expandedVersionPosition="fixed"
                    collapsedVersionPosition="fixed"
                    onVisibleHeadRefChange={setFixedHeaderElement}
                />
            </>
        );
    }

    let collapsedVersionPosition: PageHeadMobilePosition = 'static';
    switch (strategy) {
        case 'alwaysCollapsedSticky':
            collapsedVersionPosition = 'sticky';
            break;
        case 'expandedStaticCollapsedFixed':
            collapsedVersionPosition = 'fixed';
            break;
    }

    return (
        <PageHeadMobile
            {...props}
            ref={intersectionListenerTargetRef}
            isCollapsedVersionEnabled={isCollapsedVersionEnabled}
            isFullVersionEnabled={isFullVersionEnabled}
            isFullVersionVisible={isFullVersionVisible}
            isCollapsibleBehaviour={isCollapsibleBehaviour}
            expandedVersionPosition="static"
            collapsedVersionPosition={collapsedVersionPosition}
        />
    );
});

type PageHeadMobilePosition = 'static' | 'fixed' | 'sticky';

type PageHeadMobileProps = Omit<PageHeadProps, 'strategy' | 'scrollableRef' | 'onEditClick'> & {
    /**
     * Нужно рендерить свернутую шапку
     */
    isCollapsedVersionEnabled: boolean;
    /**
     * Нужно рендерить развернутую шапку
     */
    isFullVersionEnabled: boolean;
    /**
     * Видимость развернутой шапки
     */
    isFullVersionVisible: boolean;
    /**
     * Шапка сворачивается при скроле
     */
    isCollapsibleBehaviour: boolean;
    /**
     * Позиционирование шапки при развернутом состоянии
     */
    expandedVersionPosition: PageHeadMobilePosition;
    /**
     * Позиционирование шапки при свернутом состоянии
     */
    collapsedVersionPosition: PageHeadMobilePosition;
    /**
     * Коллбек изменения ref видимой шапки */
    onVisibleHeadRefChange?: (node: HTMLDivElement | null) => void;
    'aria-hidden'?: boolean;
};

const PageHeadMobile = genericMemo(
    forwardRef<HTMLDivElement, PageHeadMobileProps>(function PageHeadMobile(props, ref) {
        const {
            title,
            navbarTitle,
            caption,
            breadcrumbs,
            buttonProps,
            labels,
            help,
            addonLeft,
            addonRight,
            className,
            renderMenu,
            renderTabMenu,
            image,
            isCollapsedVersionEnabled,
            isFullVersionEnabled,
            isFullVersionVisible,
            isCollapsibleBehaviour,
            expandedVersionPosition = 'static',
            'data-testid': dataTestId = 'PageHead',
            'aria-hidden': propAriaHidden,
            onVisibleHeadRefChange,
        } = props;

        const collapsedHeadRef = useRef<HTMLDivElement | null>(null);
        const fullHeadRef = useRef<HTMLDivElement | null>(null);

        const skinClass = getColorSkin('gray-neutral', {});

        const getMenu = useCallback(
            (menuClassName: string): ReactNode =>
                renderMenu?.({
                    trigger: (
                        <Button
                            color="text"
                            size="m"
                            icon={IconVerticalMenu}
                            className={menuClassName}
                            data-testid={`${dataTestId}.MenuTrigger`}
                            logNode="PageHeadMenuTrigger"
                        />
                    ),
                    'data-testid': `${dataTestId}.Menu`,
                    size: 'm',
                }),
            [renderMenu, dataTestId],
        );

        const hasMenu = Boolean(renderMenu);
        const hasNavbarTitle = Boolean(navbarTitle);

        /*
         * Балансировка actionsRow: количество слотов слева и справа должно совпадать.
         * Слева — реальные addonLeft (1 или 2), справа — меню (`renderMenu`) и/или `addonRight`.
         * Если одна сторона короче — недостающая добивается пустышками через `addonPlaceholder`.
         * Плейсхолдеры добавляются в противоположный от реальных слотов конец: слева — в хвост,
         * справа — в начало, чтобы реальные элементы прижались к внешним краям строки.
         */
        const { buttons, hasButtons, addonLefts, hasAddonLeft, rightSlots, leftPad, rightPad } = useMemo(() => {
            const normalizedButtons = normalizeButtons(buttonProps);
            const normalizedAddonLefts = normalizeAddonLeft(addonLeft);

            const collectedRightSlots: ReactNode[] = [];
            if (renderMenu) {
                collectedRightSlots.push(getMenu(cls('menu')));
            }
            if (addonRight) {
                collectedRightSlots.push(<div>{addonRight}</div>);
            }

            const slotsCount = Math.max(normalizedAddonLefts.length, collectedRightSlots.length);

            return {
                buttons: normalizedButtons,
                hasButtons: normalizedButtons.length > 0,
                addonLefts: normalizedAddonLefts,
                hasAddonLeft: normalizedAddonLefts.length > 0,
                rightSlots: collectedRightSlots,
                leftPad: slotsCount - normalizedAddonLefts.length,
                rightPad: slotsCount - collectedRightSlots.length,
            };
        }, [buttonProps, addonLeft, addonRight, renderMenu, getMenu]);

        const titleAddon = useCallback((helpProps: HelpProps) => help && help(helpProps), [help]);

        useEffect(() => {
            if (isFullVersionVisible) {
                onVisibleHeadRefChange?.(isFullVersionEnabled ? fullHeadRef.current : null);
                return;
            }

            onVisibleHeadRefChange?.(isCollapsedVersionEnabled ? collapsedHeadRef.current : null);
            return;
        }, [isFullVersionEnabled, isCollapsedVersionEnabled, isFullVersionVisible, onVisibleHeadRefChange]);

        return (
            <>
                {isCollapsedVersionEnabled && (
                    <PageHeadCollapsed ref={collapsedHeadRef} {...props} isVisible={!isFullVersionVisible} />
                )}
                {isFullVersionEnabled && (
                    <PageHeadWrapper
                        className={cls(
                            {
                                position: expandedVersionPosition,
                                hidden: !isFullVersionVisible,
                                collapsibleBehaviour: isCollapsibleBehaviour,
                            },
                            [className, skinClass],
                        )}
                        ref={mergeRefs(ref, fullHeadRef)}
                        data-testid={dataTestId}
                        aria-hidden={propAriaHidden || (isCollapsedVersionEnabled && !isFullVersionVisible)}
                    >
                        {Boolean(title) && (hasAddonLeft || hasNavbarTitle) && (
                            <HStack className={cls('actionsRow')}>
                                {/* левая группа кнопок */}
                                {hasAddonLeft && (
                                    <HStack gap="4">
                                        {addonLefts.map((fn, index) => (
                                            <Fragment key={`left-${index}`}>
                                                {fn({ size: 'm', color: 'text' })}
                                            </Fragment>
                                        ))}
                                        {Array.from({ length: leftPad }, (_, index) => (
                                            <Fragment key={`left-pad-${index}`}>
                                                {addonPlaceholder({ size: 'm', color: 'text' })}
                                            </Fragment>
                                        ))}
                                    </HStack>
                                )}
                                {/* navbar title по центру */}
                                {hasNavbarTitle && (
                                    <HStack
                                        justifyContent="center"
                                        alignItems="center"
                                        flex="1 1 auto"
                                        minWidth="0px"
                                        minHeight="44px"
                                    >
                                        <TextGeneric
                                            data-testid={`${dataTestId}.NavbarTitle`}
                                            textSize="16-24-M"
                                            bold
                                            color="current"
                                            isTruncated
                                        >
                                            {navbarTitle}
                                        </TextGeneric>
                                    </HStack>
                                )}
                                {/* правая группа кнопок */}
                                {hasAddonLeft && (
                                    <HStack className={cls('rightAddon')} gap="4">
                                        {Array.from({ length: rightPad }, (_, index) => (
                                            <Fragment key={`right-pad-${index}`}>
                                                {addonPlaceholder({ size: 'm', color: 'text' })}
                                            </Fragment>
                                        ))}
                                        {rightSlots.map((node, index) => (
                                            <Fragment key={`right-${index}`}>{node}</Fragment>
                                        ))}
                                    </HStack>
                                )}
                            </HStack>
                        )}
                        {Boolean(title) && (
                            <VStack
                                gap="20"
                                className={cls('content', {
                                    hasTabMenu: Boolean(renderTabMenu),
                                    paddingCorrectionBottom: hasMenu && !hasButtons,
                                })}
                            >
                                <HStack gap="8">
                                    <VStack gap="8">
                                        {breadcrumbs && (
                                            <Breadcrumbs size="xs" list={breadcrumbs} className={cls('breadcrumbs')} />
                                        )}
                                        <HStack gap="12" alignItems={caption || labels ? 'flex-start' : 'center'}>
                                            <PageHeadImage image={image} />
                                            <VStack gap="6">
                                                {title && (
                                                    <TextGenericWithSkin
                                                        data-testid={`${dataTestId}.Title`}
                                                        skinSize="l"
                                                        textSize="current-main"
                                                        bold
                                                        color="current"
                                                        help={titleAddon}
                                                        className={cls('header')}
                                                        as="h2"
                                                        maxLines={3}
                                                    >
                                                        {title}
                                                    </TextGenericWithSkin>
                                                )}
                                                {labels && (
                                                    <div>
                                                        {labels?.({
                                                            labelSize: '2xs',
                                                            iconSize: '12',
                                                        })}
                                                    </div>
                                                )}
                                                {caption && (
                                                    <TextGeneric
                                                        textSize="12-16"
                                                        color="current"
                                                        role="supplementary"
                                                        className={cls('labelCaption')}
                                                    >
                                                        {caption}
                                                    </TextGeneric>
                                                )}
                                            </VStack>
                                        </HStack>
                                    </VStack>
                                    {!hasAddonLeft && (hasMenu || addonRight) && (
                                        <HStack
                                            className={cls('rightAddon', {
                                                compensateTop: Boolean(breadcrumbs),
                                            })}
                                        >
                                            {hasMenu && getMenu(cls('menu'))}
                                            {addonRight}
                                        </HStack>
                                    )}
                                </HStack>
                                {hasButtons && (
                                    <VStack
                                        gap="8"
                                        className={cls('buttonWrapper', {
                                            compensateTop:
                                                Boolean(hasMenu || addonRight) && !caption && !labels && !addonLeft,
                                        })}
                                    >
                                        {buttons.map((props, index) => (
                                            <Button
                                                // Делаем разные ключи для кнопок и ссылок, так как c одинаковым ключем
                                                // react может попытаться обновить компонент, а не строить заново.
                                                // Обновление с другим набором пропсов (href, onClick)
                                                // может привести к ошибкам в react-aria.
                                                key={`${props.href ? 'link' : 'button'}-${index}`}
                                                data-testid={
                                                    index === 0
                                                        ? `${dataTestId}.MainActionButton`
                                                        : `${dataTestId}.SecondaryActionButton`
                                                }
                                                color="contour"
                                                {...props}
                                                size="m"
                                                width="max"
                                            />
                                        ))}
                                    </VStack>
                                )}
                            </VStack>
                        )}
                        {renderTabMenu && (
                            <div
                                className={cls('tabMenuWrapper', {
                                    compensateTop: Boolean(hasMenu || addonRight) && !hasButtons && !addonLeft,
                                })}
                            >
                                {renderTabMenu({
                                    hasTopPadding: true,
                                    width: 'max',
                                    isUnderlined: false,
                                    'data-testid': `${dataTestId}.TabMenu`,
                                    className: cls('tabMenu'),
                                    tabsClassName: cls('tabMenuTabs'),
                                    logNode: 'PageHeadTabMenu',
                                })}
                            </div>
                        )}
                    </PageHeadWrapper>
                )}
            </>
        );
    }),
);

const addonPlaceholder = (props: ButtonProps) => {
    const sizeSkinClass = getSizeSkin(props.size);

    return <div className={cls('addonLeftPlaceholder', [sizeSkinClass])} />;
};

type PageHeadCollapsedProps = PageHeadMobileProps & { isVisible?: boolean };
const PageHeadCollapsed = forwardRef<HTMLDivElement, PageHeadCollapsedProps>(function PageHead(props, ref) {
    const {
        title,
        caption,
        breadcrumbs,
        labels,
        help,
        addonLeft: propsAddonLeft,
        addonRight,
        renderTabMenu,
        renderMenu,
        className,
        collapsedVersionPosition = 'fixed',
        isCollapsibleBehaviour,
        topOffset,
        'data-testid': propsDataTestId = 'PageHead',
        'aria-hidden': propAriaHidden,
    } = props;

    const skinClass = getColorSkin('gray-neutral', {});
    const dataTestId = `${propsDataTestId}.Collapsed`;
    const menuProps: RenderMenuProps = useMemo(
        () => ({
            trigger: (
                <Button
                    color="text"
                    size="m"
                    icon={IconVerticalMenu}
                    className={cls('menu')}
                    data-testid={`${dataTestId}.MenuTrigger`}
                    logNode="PageHeadMenuTrigger"
                />
            ),
            size: 'm',
            'data-testid': dataTestId,
        }),
        [dataTestId],
    );
    const menu = renderMenu?.(menuProps);

    /*
     * Балансировка actionsRow в свёрнутой шапке: количество слотов слева и справа должно совпадать.
     * Слева — реальные addonLeft (1 или 2), справа — меню (`renderMenu`) и/или `addonRight`.
     * Если одна сторона короче — недостающая добивается пустышками через `addonPlaceholder`.
     * Плейсхолдеры добавляются в противоположный от реальных слотов конец: слева — в хвост,
     * справа — в начало, чтобы реальные элементы прижались к внешним краям строки.
     */
    const { paddedLeft, paddedRight, slotsCount } = useMemo(() => {
        const normalizedAddonLefts = normalizeAddonLeft(propsAddonLeft);

        const collectedRightSlots: ReactNode[] = [];
        if (menu) {
            collectedRightSlots.push(menu);
        }
        if (addonRight) {
            collectedRightSlots.push(addonRight);
        }

        const totalSlots = Math.max(normalizedAddonLefts.length, collectedRightSlots.length);
        const leftPadCount = totalSlots - normalizedAddonLefts.length;
        const rightPadCount = totalSlots - collectedRightSlots.length;

        const left: PageHeadAddon[] = [
            ...normalizedAddonLefts,
            ...Array.from({ length: leftPadCount }, () => addonPlaceholder),
        ];
        const right: ReactNode[] = [
            ...Array.from({ length: rightPadCount }, () =>
                addonPlaceholder({
                    size: 'm',
                    color: 'text-supplementary',
                    logNode: 'PageHeadAddonPlaceholder',
                }),
            ),
            ...collectedRightSlots,
        ];

        return {
            paddedLeft: left,
            paddedRight: right,
            slotsCount: totalSlots,
        };
    }, [propsAddonLeft, addonRight, menu]);

    return (
        <PageHeadWrapper
            className={cls(
                {
                    collapsed: true,
                    visible: props.isVisible,
                    position: collapsedVersionPosition,
                    collapsibleBehaviour: isCollapsibleBehaviour,
                },
                [className, skinClass],
            )}
            ref={ref}
            data-testid={dataTestId}
            aria-hidden={propAriaHidden || !props.isVisible}
            topOffset={props.isVisible ? topOffset : undefined}
        >
            {Boolean(title) && (
                <HStack gap="4" className={cls('contentWrapper')}>
                    {slotsCount > 0 && (
                        <HStack>
                            {paddedLeft.map((fn, index) => (
                                <Fragment key={index}>
                                    {fn({
                                        size: 'm',
                                        color: 'text',
                                        logNode: 'PageHeadAddonLeft',
                                    })}
                                </Fragment>
                            ))}
                        </HStack>
                    )}
                    <VStack justifyContent="center" alignItems="center" className={cls('content')}>
                        {breadcrumbs && <Breadcrumbs size="xs" list={breadcrumbs} className={cls('breadcrumbs')} />}
                        {title && (
                            <TextGeneric
                                data-testid={`${dataTestId}.Title`}
                                textSize="16-24-M"
                                bold
                                color="current"
                                help={help}
                                className={cls('header')}
                                as="h2"
                                isTruncated
                            >
                                {title}
                            </TextGeneric>
                        )}
                        {(labels || caption) && (
                            <HStack
                                gap="6"
                                alignItems="center"
                                className={cls('labelCaptionContainer')}
                                justifyContent="center"
                            >
                                {labels && (
                                    <div className={cls('labelCaptionLabels')}>
                                        {labels({ labelSize: '4xs', iconSize: '8' })}
                                    </div>
                                )}
                                {caption && (
                                    <TextGeneric
                                        textSize="12-16"
                                        color="current"
                                        role="supplementary"
                                        className={cls('labelCaption')}
                                    >
                                        {caption}
                                    </TextGeneric>
                                )}
                            </HStack>
                        )}
                    </VStack>
                    {slotsCount > 0 && (
                        <HStack>
                            {paddedRight.map((node, index) => (
                                <Fragment key={index}>{node}</Fragment>
                            ))}
                        </HStack>
                    )}
                </HStack>
            )}
            {renderTabMenu && (
                <div className={cls('tabMenuWrapper')}>
                    {renderTabMenu({
                        hasTopPadding: false,
                        width: 'max',
                        isUnderlined: false,
                        'data-testid': `${dataTestId}.TabMenu`,
                        className: cls('tabMenu'),
                        tabsClassName: cls('tabMenuTabs'),
                        logNode: 'PageHeadTabMenu',
                    })}
                </div>
            )}
        </PageHeadWrapper>
    );
});

export default PageHead;
